'use strict'
import { useMemo } from 'react'

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']

function DeltaCell ({ scenario, baseline }) {
  const delta = scenario - baseline
  const color = delta > 0 ? '#a9341d' : delta < 0 ? '#2f7a3e' : 'inherit'
  const prefix = delta > 0 ? '+' : ''
  return (
    <span>
      {Math.round(scenario)}/{Math.round(baseline)}
      {' '}
      <span style={{ fontSize: '0.7rem', color }}>({prefix}{Math.round(delta)})</span>
    </span>
  )
}

function WeekTable ({ label, week }) {
  const { dates = [], scenario = [], baseline = [] } = week || {}

  const summaryRow = useMemo(() => {
    const sum = (arr, key) => arr.reduce((t, d) => t + (Number(d[key]) || 0), 0)
    const sStops = sum(scenario, 'stops')
    const bStops = sum(baseline, 'stops')
    const sService = sum(scenario, 'serviceMin')
    const bService = sum(baseline, 'serviceMin')
    const sActualService = sum(scenario, 'actualServiceMin') || sService
    const bActualService = sum(baseline, 'actualServiceMin') || bService
    const sDrive = sum(scenario, 'driveMin')
    const bDrive = sum(baseline, 'driveMin')
    const sTotal = sum(scenario, 'totalMin')
    const bTotal = sum(baseline, 'totalMin')
    const sOT = sum(scenario, 'otMin')
    const bOT = sum(baseline, 'otMin')
    return { sStops, bStops, sService, bService, sActualService, bActualService, sDrive, bDrive, sTotal, bTotal, sOT, bOT }
  }, [scenario, baseline])

  const pctChange = (s, b) => {
    if (!b) return s > 0 ? '+100%' : '–'
    const pct = ((s - b) / b) * 100
    return `${pct > 0 ? '+' : ''}${Math.round(pct)}%`
  }

  const HEADERS = ['Day', 'Stops (S/B)', 'Service (S/B)', 'Actual Svc (S/B)', 'Drive (S/B)', 'Total (S/B)', 'OT (S/B)', 'Source']

  return (
    <div className="rw-card" style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: 'var(--df-ink)' }}>{label}</div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>{HEADERS.map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {dates.map((dateStr, i) => {
              const s = scenario[i] || {}
              const b = baseline[i] || {}
              const dayName = DAY_NAMES[i] || ''
              const source = b.source || 'estimated'
              return (
                <tr key={dateStr}>
                  <td>{dayName} {dateStr.slice(5)}</td>
                  <td><DeltaCell scenario={s.stops || 0} baseline={b.stops || 0} /></td>
                  <td><DeltaCell scenario={s.serviceMin || 0} baseline={b.serviceMin || 0} /></td>
                  <td><DeltaCell scenario={s.actualServiceMin || s.serviceMin || 0} baseline={b.actualServiceMin || b.serviceMin || 0} /></td>
                  <td><DeltaCell scenario={s.driveMin || 0} baseline={b.driveMin || 0} /></td>
                  <td><DeltaCell scenario={s.totalMin || 0} baseline={b.totalMin || 0} /></td>
                  <td><DeltaCell scenario={s.otMin || 0} baseline={b.otMin || 0} /></td>
                  <td>
                    {source === 'snapshot'
                      ? <span className="rw-pill" style={{ background: '#dceedf', color: '#2f7a3e', fontWeight: 600 }}>snapshot</span>
                      : <span className="rw-pill" style={{ background: 'var(--df-surface-2)', color: 'var(--df-ink-3)' }}>estimated</span>
                    }
                  </td>
                </tr>
              )
            })}
            <tr style={{ background: 'var(--df-surface-2)', fontWeight: 600 }}>
              <td>Total</td>
              <td>{Math.round(summaryRow.sStops)}/{Math.round(summaryRow.bStops)} <span style={{ fontSize: '0.7rem' }}>({pctChange(summaryRow.sStops, summaryRow.bStops)})</span></td>
              <td>{Math.round(summaryRow.sService)}/{Math.round(summaryRow.bService)} <span style={{ fontSize: '0.7rem' }}>({pctChange(summaryRow.sService, summaryRow.bService)})</span></td>
              <td>{Math.round(summaryRow.sActualService)}/{Math.round(summaryRow.bActualService)} <span style={{ fontSize: '0.7rem' }}>({pctChange(summaryRow.sActualService, summaryRow.bActualService)})</span></td>
              <td>{Math.round(summaryRow.sDrive)}/{Math.round(summaryRow.bDrive)} <span style={{ fontSize: '0.7rem' }}>({pctChange(summaryRow.sDrive, summaryRow.bDrive)})</span></td>
              <td>{Math.round(summaryRow.sTotal)}/{Math.round(summaryRow.bTotal)} <span style={{ fontSize: '0.7rem' }}>({pctChange(summaryRow.sTotal, summaryRow.bTotal)})</span></td>
              <td>{Math.round(summaryRow.sOT)}/{Math.round(summaryRow.bOT)} <span style={{ fontSize: '0.7rem' }}>({pctChange(summaryRow.sOT, summaryRow.bOT)})</span></td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function TwoWeekView ({ comparison, crewDisplayName }) {
  if (!comparison || comparison.level !== 'two-week') return null

  const headerName = crewDisplayName
    ? crewDisplayName(comparison.crewId, comparison.crewName)
    : (comparison.crewName || comparison.crewId)

  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: 'var(--df-ink)' }}>
        Two-Week View — {headerName}
        <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--df-ink-3)', fontWeight: 400 }}>
          Starting {comparison.weekOf}
        </span>
      </div>
      <WeekTable label='A-Week' week={comparison.aWeek} />
      <WeekTable label='B-Week' week={comparison.bWeek} />
    </div>
  )
}
